/**
 * JSON-LD structured-data builders (schema.org).
 *
 * Every function here is PURE: it takes plain domain values and returns a plain
 * JSON-LD object (or null when there isn't enough data to emit a valid node).
 * `PageHead` serialises these into `<script type="application/ld+json">` tags via
 * `mapJsonLdToHeadTags` (see `seoHead.ts`). Keeping them pure makes the mapping
 * unit-testable and safe to render on any page type.
 *
 * Nodes cover the page types called out in the SEO/AEO spec:
 *   - Organization + WebSite  (site-wide identity)
 *   - Product / TouristTrip   (tour/product detail, with Offer + AggregateRating)
 *   - BlogPosting / Article   (blog posts, CMS articles)
 *   - BreadcrumbList          (navigable pages)
 *   - FAQPage                 (pages/posts with an FAQ section)
 */
import { SiteMeta, absoluteUrl } from "./siteMeta";

/** A JSON-LD node (loosely typed — schema.org shapes vary per @type). */
export type JsonLd = Record<string, any>;

const CONTEXT = "https://schema.org";

/** Drop undefined/null/empty-string/empty-array keys so nodes stay clean. */
const prune = <T extends Record<string, any>>(obj: T): T => {
    const out: Record<string, any> = {};
    Object.entries(obj).forEach(([k, v]) => {
        if (v === undefined || v === null || v === "") return;
        if (Array.isArray(v) && v.length === 0) return;
        out[k] = v;
    });
    return out as T;
};

/** Strip HTML tags + collapse whitespace so descriptions are plain text. */
export const stripHtml = (html?: string | null): string =>
    (html ?? "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/gi, " ")
        .replace(/\s+/g, " ")
        .trim();

/** Site-wide Organization node (name, url, logo, social profiles). */
export const buildOrganizationJsonLd = (site: SiteMeta): JsonLd =>
    prune({
        "@context": CONTEXT,
        "@type": "Organization",
        name: site.name,
        url: site.siteUrl || undefined,
        logo: absoluteUrl(site.logoUrl, site.siteUrl),
        sameAs: site.sameAs,
    });

/** Site-wide WebSite node, incl. the SearchAction (sitelinks search box). */
export const buildWebSiteJsonLd = (site: SiteMeta): JsonLd => {
    const node: JsonLd = {
        "@context": CONTEXT,
        "@type": "WebSite",
        name: site.name,
        url: site.siteUrl || undefined,
    };
    if (site.siteUrl) {
        node.potentialAction = {
            "@type": "SearchAction",
            target: `${site.siteUrl}/search-result?name={search_term_string}`,
            "query-input": "required name=search_term_string",
        };
    }
    return prune(node);
};

export interface BreadcrumbEntry {
    name: string;
    /** Path or absolute URL. Relative paths are resolved against site origin. */
    url?: string;
}

/** BreadcrumbList for navigable pages. Returns null when < 1 crumb. */
export const buildBreadcrumbJsonLd = (
    crumbs: BreadcrumbEntry[],
    site: SiteMeta,
): JsonLd | null => {
    const items = (crumbs ?? []).filter((c) => c && c.name);
    if (!items.length) return null;
    return {
        "@context": CONTEXT,
        "@type": "BreadcrumbList",
        itemListElement: items.map((c, i) =>
            prune({
                "@type": "ListItem",
                position: i + 1,
                name: c.name,
                item: absoluteUrl(c.url, site.siteUrl),
            }),
        ),
    };
};

export interface FaqEntry {
    question?: string;
    answer?: string;
}

/** FAQPage node from question/answer pairs. Null when none are usable. */
export const buildFaqJsonLd = (entries: FaqEntry[]): JsonLd | null => {
    const items = (entries ?? [])
        .map((e) => ({ q: (e.question ?? "").trim(), a: stripHtml(e.answer) }))
        .filter((e) => e.q && e.a);
    if (!items.length) return null;
    return {
        "@context": CONTEXT,
        "@type": "FAQPage",
        mainEntity: items.map((e) => ({
            "@type": "Question",
            name: e.q,
            acceptedAnswer: { "@type": "Answer", text: e.a },
        })),
    };
};

export interface ProductJsonLdInput {
    name: string;
    description?: string;
    /** Absolute or root-relative image URL(s). */
    images?: string[];
    /** Canonical/product URL (absolute or path). */
    url?: string;
    /** SKU / tour code. */
    sku?: string | number;
    brandName?: string;
    /** Lowest/representative price. */
    price?: number | string;
    priceCurrency?: string;
    /** schema.org availability, e.g. "https://schema.org/InStock". */
    availability?: string;
    /** ISO-8601 duration, e.g. "P3D" — see SEO spec gap note (backend field). */
    isoDuration?: string;
    ratingValue?: number | string;
    ratingCount?: number | string;
}

const DEFAULT_CURRENCY = "AUD";

const toOffer = (input: ProductJsonLdInput, site: SiteMeta): JsonLd | undefined => {
    const priceNum = Number(input.price);
    if (!input.price || isNaN(priceNum) || priceNum <= 0) return undefined;
    return prune({
        "@type": "Offer",
        price: priceNum,
        priceCurrency: input.priceCurrency || DEFAULT_CURRENCY,
        availability: input.availability || "https://schema.org/InStock",
        url: absoluteUrl(input.url, site.siteUrl),
    });
};

const toAggregateRating = (input: ProductJsonLdInput): JsonLd | undefined => {
    const value = Number(input.ratingValue);
    if (!input.ratingValue || isNaN(value) || value <= 0) return undefined;
    const count = Number(input.ratingCount);
    return prune({
        "@type": "AggregateRating",
        ratingValue: value,
        // Google requires a count; default to 1 when the backend omits it.
        reviewCount: !isNaN(count) && count > 0 ? count : 1,
    });
};

/** Product node for a tour/product detail page (Offer + AggregateRating). */
export const buildProductJsonLd = (input: ProductJsonLdInput, site: SiteMeta): JsonLd | null => {
    if (!input.name) return null;
    const images = (input.images ?? [])
        .map((img) => absoluteUrl(img, site.siteUrl))
        .filter(Boolean) as string[];
    return prune({
        "@context": CONTEXT,
        "@type": "Product",
        name: input.name,
        description: stripHtml(input.description) || undefined,
        image: images,
        sku: input.sku !== undefined && input.sku !== "" ? String(input.sku) : undefined,
        brand: input.brandName ? { "@type": "Brand", name: input.brandName } : undefined,
        offers: toOffer(input, site),
        aggregateRating: toAggregateRating(input),
    });
};

/** TouristTrip node — the travel-specific complement to Product for tours. */
export const buildTouristTripJsonLd = (
    input: ProductJsonLdInput,
    site: SiteMeta,
): JsonLd | null => {
    if (!input.name) return null;
    const images = (input.images ?? [])
        .map((img) => absoluteUrl(img, site.siteUrl))
        .filter(Boolean) as string[];
    return prune({
        "@context": CONTEXT,
        "@type": "TouristTrip",
        name: input.name,
        description: stripHtml(input.description) || undefined,
        image: images,
        url: absoluteUrl(input.url, site.siteUrl),
        offers: toOffer(input, site),
        aggregateRating: toAggregateRating(input),
    });
};

export interface ArticleJsonLdInput {
    headline: string;
    description?: string;
    url?: string;
    image?: string;
    authorName?: string;
    datePublished?: string | null;
    dateModified?: string | null;
    /** "BlogPosting" (default) or "Article". */
    type?: "BlogPosting" | "Article";
}

/** BlogPosting/Article node for a blog post or CMS article. */
export const buildArticleJsonLd = (
    input: ArticleJsonLdInput,
    site: SiteMeta,
): JsonLd | null => {
    if (!input.headline) return null;
    return prune({
        "@context": CONTEXT,
        "@type": input.type || "BlogPosting",
        headline: input.headline,
        description: stripHtml(input.description) || undefined,
        image: absoluteUrl(input.image, site.siteUrl),
        mainEntityOfPage: absoluteUrl(input.url, site.siteUrl),
        author: input.authorName
            ? { "@type": "Person", name: input.authorName }
            : { "@type": "Organization", name: site.name },
        publisher: prune({
            "@type": "Organization",
            name: site.name,
            logo: prune({ "@type": "ImageObject", url: absoluteUrl(site.logoUrl, site.siteUrl) }),
        }),
        datePublished: input.datePublished || undefined,
        dateModified: input.dateModified || input.datePublished || undefined,
    });
};
